<template>
  <transition name="update-banner">
    <div
      v-if="visible"
      class="update-banner"
      :class="{ 'update-banner--over-tabs': overTabBar }"
      role="status"
      aria-live="polite"
    >
      <img src="/assets/logo/syntry-mark.svg" class="update-banner__mark" alt="" aria-hidden="true" />
      <p class="update-banner__text">A new version of Syntry is available.</p>
      <div class="update-banner__actions">
        <button
          type="button"
          class="update-banner__btn update-banner__btn--ghost"
          :disabled="updating"
          @click="onLater"
        >
          Later
        </button>
        <button
          type="button"
          class="update-banner__btn update-banner__btn--primary"
          :disabled="updating"
          @click="onUpdate"
        >
          <ion-spinner v-if="updating" name="crescent" class="update-banner__spinner" />
          <span v-else>Update</span>
        </button>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * The PWA update prompt, plus the one-off "ready to work offline" toast.
 *
 * A persistent banner rather than a modal or a toast, deliberately:
 *  - a modal would block a half-finished leave request or timesheet entry for
 *    something that is never urgent;
 *  - a `duration: 0` toast would collide with the app's dozen transient toasts
 *    and cannot be anchored above the custom tab bar on standalone routes.
 *
 * Tapping Update hands over to the "Updating Syntry…" wall and, after the
 * reload, the "Update complete" toast — both owned by UpdateProgressHost.vue
 * (`applyUpdate()` in register.ts starts the session).
 *
 * Mounted in App.vue, so it survives every navigation and is reachable on both
 * /tabs/* and the standalone pages.
 */
import { computed, ref, watch } from 'vue';
import { IonSpinner } from '@ionic/vue';
import { useRoute } from 'vue-router';
import { applyUpdate, pwaNeedRefresh, pwaOfflineReady } from '@/pwa/register';
import { updateInProgress } from '@/pwa/update-session';
import { updateAvailable } from '@/pwa/version-check';
import { isComposerRoute, isTabBarVisible } from '@/utils/tab-bar';
import { useToast } from '@/composables/useToast';

/** Set once the "ready to work offline" toast has been shown, ever. */
const OFFLINE_TOASTED_KEY = 'syntry.pwa_offline_toasted';

const route = useRoute();
const toaster = useToast();
const updating = ref(false);
/**
 * "Later" only lasts for this page session. The waiting worker doesn't go
 * away, so `onNeedRefresh` fires again on the next cold start and the banner
 * returns — no persistence needed, and no way to permanently miss an update.
 */
const dismissed = ref(false);

// A newer worker arriving after a dismissal re-arms the banner.
watch(pwaNeedRefresh, (now, was) => {
  if (now && !was) dismissed.value = false;
});

const overTabBar = computed(() => isTabBarVisible(route.path));
const visible = computed(
  () =>
    pwaNeedRefresh.value &&
    !dismissed.value &&
    !isComposerRoute(route.path) &&
    // The version watchdog's modal is the stronger prompt for the same event —
    // never show both (UpdateRequiredHost.vue).
    !updateAvailable.value &&
    // The update wall is up (from here or from the watchdog) — nothing to offer.
    !updateInProgress.value,
);

function onLater(): void {
  dismissed.value = true;
}

async function onUpdate(): Promise<void> {
  if (updating.value) return;
  updating.value = true;
  // Puts up the "Updating Syntry…" wall, which stashes the handoff the reloaded
  // page confirms the jump with (update-session.ts).
  await applyUpdate();
}

async function toast(message: string): Promise<void> {
  try {
    await toaster.success(message);
  } catch {
    /* no-op */
  }
}

// First successful install — tell the user once, ever, that offline works.
watch(pwaOfflineReady, async (ready) => {
  if (!ready) return;
  try {
    if (localStorage.getItem(OFFLINE_TOASTED_KEY)) return;
    localStorage.setItem(OFFLINE_TOASTED_KEY, '1');
  } catch {
    return; // can't remember it → don't risk repeating it
  }
  await toast('Syntry is ready to work offline.');
});
</script>

<style scoped lang="scss">
.update-banner {
  position: fixed;
  left: var(--sp-4);
  right: var(--sp-4);
  bottom: calc(var(--syntry-safe-bottom, env(safe-area-inset-bottom, 0px)) + var(--sp-4));
  max-width: 480px;
  margin-inline: auto;
  /* Above the tab bar (100), below the offline banner (300) and far below
     Ionic's overlays, so a modal always covers this. */
  z-index: 200;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-3) var(--sp-4);
  border-radius: var(--syntry-radius-card);
  /* Frosted surface, matching ConfirmModal. */
  background: rgba(var(--syntry-surface-rgb), 0.92);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid var(--syntry-border);
  box-shadow: var(--syntry-shadow-elevated);

  /* Clear the bottom tab bar when it's on screen. */
  &--over-tabs {
    bottom: calc(
      64px + var(--syntry-safe-bottom, env(safe-area-inset-bottom, 0px)) + var(--sp-3)
    );
  }

  &__mark {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: var(--syntry-fs-tiny);
    font-weight: 600;
    color: var(--syntry-text-strong);
    line-height: 1.35;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    flex-shrink: 0;
  }

  &__btn {
    min-height: 34px;
    padding: 0 var(--sp-3);
    border-radius: var(--syntry-radius-button);
    border: none;
    font-family: var(--syntry-font);
    font-size: var(--syntry-fs-tiny);
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:disabled { opacity: 0.6; cursor: not-allowed; }

    &--ghost {
      background: transparent;
      color: var(--syntry-text-muted);
    }

    &--primary {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 74px;
      background: var(--syntry-primary);
      color: #ffffff;
      box-shadow: var(--syntry-shadow-button);
    }
  }

  &__spinner {
    width: 16px;
    height: 16px;
    color: #ffffff;
  }
}

.update-banner-enter-active,
.update-banner-leave-active {
  transition: transform 220ms ease, opacity 220ms ease;
}

.update-banner-enter-from,
.update-banner-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .update-banner-enter-active,
  .update-banner-leave-active {
    transition: none;
  }
}
</style>
